import { Alert, Button, Select, Space, Table, Tooltip, Row, Col, Checkbox, Modal, notification } from "antd";
import { CheckCircleFilled, DownOutlined, UpOutlined, FilterOutlined, CloudUploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { getElasticSearchService } from "../../backend";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { Dictionary, EnvDeployment, ExecutingTemplate, IEnvDetails } from "../../types";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import Heading from "../../components/Heading";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import { mapAppDeployments, mapInfraDeployments } from "../../utils/deployments";
import GitCommitLink from "../../components/GitCommitLink";
import MessageCard from "../../components/cards/MessageCard";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { handleUndeployEnvironment } from "../../utils/environment/handle-undeploy-environment";
import useReadLocalStorage from "../../contexts/LocalStorage";
import { fetchProjectLimitations } from "../../utils/local-storage";
import { getProjectService } from "../../backend";
import { PricingPlanNames } from "../../enums/enums";
import { getDetailedPaymentPlan } from "../../utils/payment-plans";
import { useCurrentProject } from "../../contexts/Project";


export default function EnvironmentDeployments() {
    const { projectId, envId } = useParams();
    const executingTemplates = useReadLocalStorage<ExecutingTemplate[]>("executingTemplates");
    const currentProject = useRecoilValue(currentProjectState);
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const [loadingDeployments, setLoadingDeployments] = useState(true);
    const [deployments, setDeployments] = useState<EnvDeployment[]>([]);
    const [resourceNames, setResourceNames] = useState<string[]>([]);
    const [selectedResourceNames, setSelectedResourceNames] = useState<string[]>([]);
    const [onlyActive, setOnlyActive] = useState(false);
    const [numberOfExecutingTemplates, setNumberOfExecutingTemplates] = useState<number>(0);
    const [stageNumberOfDeployments, setStageNumberOfDeployments] = useState<number>(0);
    const [projectNumberOfDeployments, setProjectNumberOfDeployments] = useState<number>(0);
    const limitations = fetchProjectLimitations();
    const navigate = useNavigate();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const [ctaMessage, setCtaMessage] = useState<{ title: string | React.ReactNode, description: string | React.ReactNode, buttonText: string }>();
    const [nextPaymentPlan, setNextPaymentPlan] = useState<any>();
    const { updateCurrentProject } = useCurrentProject();

    useEffect(() => {
        if (envDetails) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [envDetails.deployedMicroservices, envDetails.lastDeploymentId]);

    useEffect(() => {
        const executingTemplatesLength = (executingTemplates || []).filter((template: any) => template.env.id === envId).length;
        setNumberOfExecutingTemplates(executingTemplatesLength);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executingTemplates]);

    useEffect(() => { // Enable deployments history
        if (currentProject.paymentPlan?.id === "STARTER" && stageNumberOfDeployments >= 5) {
            loadNextPaymentPlan(PricingPlanNames.ADVANCED)
            setCtaMessage({
                title: "Unlock Deployment History and Insights",
                description: "Understand the impact of code changes, track progress, and gain valuable insights of your current and past deployments.",
                buttonText: "Activate Deployment Insights"
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stageNumberOfDeployments])

    useEffect(() => { // Deployments used

        const percentageOfDeploymentsUsed = projectNumberOfDeployments / limitations.deploymentsPerMonth * 100;
        const nextPaymentPlanToLoad: PricingPlanNames = currentProject.paymentPlan?.id === PricingPlanNames.FREE ?
            PricingPlanNames.STARTER : PricingPlanNames.ADVANCED;

        if (percentageOfDeploymentsUsed >= 100) {
            loadNextPaymentPlan(nextPaymentPlanToLoad);
            // deployments used
            setCtaMessage({
                title: "No more available deployments in this plan",
                description: "Accelerate your development with more deployments per month. Unlock more opportunities to release new features and bug fixes faster and keep your users engaged.",
                buttonText: "Upgrade and release more!"
            });
        } else if (percentageOfDeploymentsUsed >= 70) {
            // deployments almost used
            if (currentProject.paymentPlan?.id !== PricingPlanNames.ADVANCED) {
                loadNextPaymentPlan(nextPaymentPlanToLoad);
                setCtaMessage({
                    title: `${Math.round(percentageOfDeploymentsUsed)}% of deployments used`,
                    description: `Congratulations on reaching your monthly deployment limit! Your team is making strides in delivering your software to users. Upgrade to our ${nextPaymentPlanToLoad} plan with more deployments per month to keep the momentum going.`,
                    buttonText: "Upgrade for more deployments"
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectNumberOfDeployments])


    const loadNextPaymentPlan = async (planId: PricingPlanNames) => {
        const nextPaymentPlan = await getDetailedPaymentPlan(planId);
        setNextPaymentPlan(nextPaymentPlan);
    }

    const loadData = async () => {
        try {
            const [
                { data: { response: infraDeployments } },
                { data: { response: appDeployments } },
                { data: { response: { numberOfDeployments: projectNumberOfDeployments } } },
                { data: { response: { numberOfDeployments: stageNumberOfDeployments } } }
            ] = await Promise.all([
                getElasticSearchService().getStageDeploymentHistory(projectId!, envId!),
                getElasticSearchService().getServiceDeploymentHistory(projectId!),
                getElasticSearchService().getNumberOfDeployments(projectId!, "stage"),
                getElasticSearchService().getNumberOfDeployments(projectId!, "stage", envId!),
            ]);

            // All deployed apps across all clusters in the environment
            const allDeployedApps = envDetails.deployedMicroservices.flatMap(d => d.microservices);

            // Ignore deployments for the apps which are no longer deployed on the environment clusters
            const activeAppDeployments = appDeployments.filter(d => {
                return allDeployedApps.find(a => a.name === d.name && a.kubernetesId === d.kubernetesId) !== undefined
            });

            // Combined infrastructure and app deployments
            const allDeployments = [
                ...mapInfraDeployments(infraDeployments, envDetails.lastDeploymentId),
                ...mapAppDeployments(activeAppDeployments, allDeployedApps.map(app => ({ ...app, microserviceId: app.id })))
            ].sort((d1, d2) => Number(new Date(d2.timestamp)) - Number(new Date(d1.timestamp)));

            // Unique resource/app names deployed in the environment
            const resourceNames = new Set(allDeployments.reduce((resources, deployment) => {
                return [
                    ...resources,
                    ...deployment.resources.map(r => r.name)
                ];
            }, [] as string[]));

            setDeployments(allDeployments);
            setResourceNames(Array.from(resourceNames));
            setStageNumberOfDeployments(stageNumberOfDeployments);
            setProjectNumberOfDeployments(projectNumberOfDeployments);
        } catch (error) {
            console.log("ERROR", error);
        } finally {
            setLoadingDeployments(false);
        }
    }

    const filterDeployments = () => {
        return deployments
            .filter(d => onlyActive ? d.active === true : true)
            .filter(d => {
                if (selectedResourceNames.length) {
                    return d.resources
                        .filter(r => !r.prev?.equal)
                        .find(r => selectedResourceNames.includes(r.name)) !== undefined
                } else {
                    return true;
                }
            });
    }

    const handleSelectPlan = async () => {
        try {
            await getProjectService().changeProjectSubscription(
                currentProject!.id,
                {
                    paymentPlanId: nextPaymentPlan.id,
                    priceId: nextPaymentPlan.priceId!
                }
            );
            await updateCurrentProject(currentProject!.id);
            setCtaMessage(undefined)
            await loadData();
        } catch (error: any) {
            notification.error({
                message: `Error activating the ${nextPaymentPlan.name} plan`,
                description: <>
                    {error.response.data.message}&nbsp;
                    <a href={`/projects/${currentProject!.id}/settings/billing`}>Go to Billing</a> to check your settings.
                </>
            })
        }
    }

    const isTrialEligible = (planId: string) => {
        return currentProject?.freeTrials && !(currentProject.freeTrials as Dictionary<boolean>)[planId];
    }

    const showSelectPlanConfirm = () => {
        Modal.confirm({
            title: `Activate ${nextPaymentPlan.name} plan`,
            content:
                <Space direction='vertical'>
                    <div>
                        You are about to activate the {nextPaymentPlan.name} plan that will cost {nextPaymentPlan.price} per month.
                    </div>
                    {
                        isTrialEligible(nextPaymentPlan.id) ?
                            <div>
                                <b>7 day free trial</b> will be applied.
                            </div> :
                            <div>
                                Your credit card will be <b>charged immediately</b> because you've already used your free trial period.
                            </div>
                    }
                </Space>,
            okText: `Activate ${nextPaymentPlan.name} plan`,
            centered: true,
            async onOk() {
                return handleSelectPlan();
            }
        });
    }

    return (
        <>
            {
                ctaMessage &&
                <>
                    <MessageCard
                        style={{ maringBottom: 20 }}
                        icon={
                            <InfoCircleOutlined style={{ fontSize: 28, color: "#3399ff" }} />
                        }
                        text={
                            <div>
                                <div style={{ fontWeight: 600 }}>
                                    {ctaMessage.title}
                                </div>
                                <div>
                                    {ctaMessage.description}
                                </div>
                            </div>
                        }
                        type="cta"
                        extra={
                            <Button type="primary"
                                onClick={showSelectPlanConfirm}>
                                {ctaMessage.buttonText}
                            </Button>
                        }
                    />
                    {
                        numberOfExecutingTemplates > 0 ? <><br /><br /></> : null
                    }
                </>
            }

            {
                numberOfExecutingTemplates > 0 &&
                <Row style={{ marginTop: "-30px" }} align="top">
                    <Col xs={24}>
                        <MessageCard
                            type="info"
                            text={
                                <span>{`This environment has ${numberOfExecutingTemplates} recently executed template(s). `}<Link to={`/projects/${projectId}/environments/${envId}/active-templates`}>View details.</Link></span>
                            }
                        />
                    </Col>
                </Row>
            }

            <Heading
                title={<>
                    <CloudUploadOutlined /> Deployments
                </>}
                description="Overview of the infrastructure and application deployments for this environment."
                level={3}
            />
            <Space direction="vertical" size="large" className="full-width">
                {
                    envDetails.status === "ROLLBACK_FAILED" && envDetails.awsAccountId &&
                    <Alert
                        type="error"
                        message={
                            <Space size={5}>
                                The environment failed to rollback!
                                <Button
                                    type="link"
                                    className="no-padding"
                                    onClick={() => openConfirmModal({
                                        title: "Undeploy Infrastructure?",
                                        description: "The infrastructure resources in the environment will be permanently deleted from your cloud account.",
                                        confirmation: envId!,
                                        alert: <b>This action cannot be undone.</b>,
                                        cancelText: "Cancel",
                                        okText: "Undeploy Infrastructure",
                                        onOk: () => handleUndeployEnvironment({
                                            envId: envId!,
                                            projectId: projectId!,
                                            navigate
                                        })
                                    })}>
                                    Undeploy
                                </Button>
                                the environment and deploy the infrastructure again.
                            </Space>
                        }
                    />
                }
                {
                    envDetails.status === "UPDATE_ROLLBACK_FAILED" &&
                    <Alert
                        type="error"
                        message={
                            <>
                                The environment failed to rollback! To resolve this problem you need to <a href="https://aws.amazon.com/premiumsupport/knowledge-center/cloudformation-update-rollback-failed/" target="_blank" rel="noreferrer">continue the rollback process manually</a> from the AWS Console.
                            </>
                        }
                    />
                }

                <Table
                    pagination={false}
                    rowKey="id"
                    className="ant-table-wrapper-bordered"
                    loading={loadingDeployments}
                    dataSource={filterDeployments()}
                    expandable={{
                        expandIcon: ({ expanded, onExpand, record }) =>
                            !record.error && !record.status.errors?.length ? undefined :
                                expanded ?
                                    <UpOutlined onClick={e => onExpand(record, e)} /> :
                                    <DownOutlined onClick={e => onExpand(record, e)} />,
                        expandedRowRender: (record: any) => {
                            return (
                                record.error ?
                                    <div style={{ color: "red" }}>
                                        {record.error}
                                    </div> :
                                    <div style={{ margin: "-16px" }}>
                                        <Table
                                            showHeader={false}
                                            bordered={false}
                                            title={() =>
                                                <div className="flex-justify-space-between">
                                                    Deployment error logs
                                                </div>
                                            }
                                            dataSource={record.status.errors}
                                            columns={[
                                                {
                                                    title: "Resource ID",
                                                    render: event => <>
                                                        <div>
                                                            {event.logicalResourceId}
                                                        </div>
                                                        <div>
                                                            {event.resourceType}
                                                        </div>
                                                    </>
                                                },
                                                {
                                                    title: "Reason",
                                                    dataIndex: "statusReason"
                                                }
                                            ]}
                                            pagination={false}
                                            size="small"
                                        />
                                    </div>
                            )
                        }
                    }}
                    columns={[
                        {
                            title: "Deployment",
                            render: (deployment: EnvDeployment) => <Space size="middle">
                                <div>
                                    <Link to={
                                        deployment.type === "infrastructure" ?
                                            `/projects/${projectId}/environments/${envId}/deployments/infrastructure/${deployment.id}` :
                                            [
                                                "/projects",
                                                projectId,
                                                "environments",
                                                envId,
                                                "deployments",
                                                "apps",
                                                deployment.resources[0].kubernetesId,
                                                deployment.resources[0].kubernetesNamespace,
                                                deployment.resources[0].name,
                                                deployment.id
                                            ].join("/")
                                    }>
                                        <Space>
                                            {deployment.id}
                                            {
                                                deployment.active &&
                                                <Tooltip title="The deployment that is available to your end users.">
                                                    <CheckCircleFilled style={{
                                                        fontSize: 15,
                                                        color: "green"
                                                    }} />
                                                </Tooltip>
                                            }
                                        </Space>
                                    </Link>
                                    <div>
                                        <small>
                                            {deployment.type}
                                        </small>
                                    </div>
                                </div>
                            </Space>
                        },
                        {
                            title: 'Status',
                            key: 'status',
                            render: (deployment: EnvDeployment) => <DeploymentStatusTag status={deployment.status.name} />
                        },
                        {
                            title: 'Triggered by',
                            key: 'id',
                            render: (deployment: EnvDeployment) => <Space size="large">
                                <div>
                                    {deployment.createdBy}
                                    <div>
                                        <small>
                                            {deployment.createdAt}
                                        </small>
                                    </div>
                                </div>
                            </Space>
                        },
                        {
                            title: 'Updates',
                            width: "40%",
                            key: 'deployment',
                            render: (deployment: EnvDeployment) => deployment.resources
                                .filter(r => !r.prev?.equal)
                                .map(r => (
                                    <small key={r.name}>
                                        <div>
                                            <b>{r.name}</b> (<GitCommitLink version={r.version} repoUrl={r.repositoryUrl} commitType={r.commitType} branch={r.branch} hideIcon={true} />)
                                        </div>
                                        {
                                            deployment.type === "app" &&
                                            <div>
                                                Cluster: {r.kubernetesName}
                                                <br />
                                                Namespace: {r.kubernetesNamespace}
                                            </div>
                                        }
                                    </small>
                                ))
                        },
                        Table.EXPAND_COLUMN
                    ]}
                    title={() => (
                        <div className="flex-justify-space-between flex-align-center">
                            {
                                limitations.deploymentInsights ?
                                    <Tooltip title="The deployments that are available to your end users.">
                                        <Checkbox onChange={(e) => setOnlyActive(e.target.checked)
                                        }>Only live deployments</Checkbox>
                                        <InfoCircleOutlined />
                                    </Tooltip> : null
                            }
                            <Select
                                mode="multiple"
                                showSearch={true}
                                showArrow={true}
                                allowClear={true}
                                placeholder="Filter by updated resources"
                                onChange={setSelectedResourceNames}
                                style={{ minWidth: '30%' }}
                                suffixIcon={<FilterOutlined />}
                            >
                                {
                                    resourceNames.map(name =>
                                        <Select.Option key={name}>{name}</Select.Option>
                                    )
                                }
                            </Select>
                        </div>
                    )}
                />
            </Space >
        </>
    );
}